import { currentUser } from '@easy-expense/auth-client';
import {
  useCachedWorkspaceMembership,
  useIsAdminOrManagerRole,
  useWorkspaceStore,
} from '@easy-expense/data-firestore-client';
import { PaymentMethod, PaymentMethodValue } from '@easy-expense/data-schema-v2';
import Data from '@easy-expense/frontend-data-layer';
import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import React from 'react';

import { reassignmentCollidesWithExisting } from './reassignment';
import { Button } from '../Button.components';
import { LabelTextField } from '../LabelTextField.component';
import { useMembersAutoComplete } from '../Report/ShareReportButton';
import { AutoCompleteValue } from '../Shared/Dropdown/AutoCompleteTypes';
import { SearchDropdown } from '../Shared/Dropdown/SearchDropDown.component';

const PaymentMethodForm: React.FC<{
  onDone: () => void;
  onDiscard: () => void;
  paymentMethod: PaymentMethod | undefined;
}> = ({ onDone, onDiscard, paymentMethod }) => {
  const selectedPaymentMethodKey = paymentMethod?.key;
  const userID = currentUser()?.uid ?? '';
  const workspaceMember = useCachedWorkspaceMembership(userID);
  const defaultPaymentMethodKey = workspaceMember?.settings?.defaultPaymentMethodKey ?? '';
  const updateMemberSettings = useWorkspaceStore((state) => state.updateMemberSettings);

  const isAdminOrManager = useIsAdminOrManagerRole();
  const memberOptions = useMembersAutoComplete([]);
  // Admins and managers can assign the owner (createdBy) when creating a new payment method
  // as well as when editing an existing one. On create the picker defaults to the current
  // user, so a card is still owned by its creator unless the owner is explicitly changed.
  // With only one member to choose from there is nothing to assign, so the picker is hidden.
  const canReassign = isAdminOrManager && memberOptions.length > 1;
  const [assignedToUserID, setAssignedToUserID] = React.useState<string>(
    paymentMethod?.createdBy ?? userID,
  );
  const [duplicateErrorMessage, setDuplicateErrorMessage] = React.useState<string>('');

  const [paymentMethodValue, setPaymentMethodValue] = React.useState<PaymentMethodValue>({
    institution: paymentMethod?.value?.institution ?? '',
    type: paymentMethod?.value?.type ?? 'credit',
    name: paymentMethod?.value?.name ?? '',
    userID: paymentMethod?.value?.userID ?? userID,
    digits: paymentMethod?.value?.digits ?? '',
    isEditable: paymentMethod?.value?.isEditable ?? true,
    isDefault:
      paymentMethod?.key === defaultPaymentMethodKey ||
      (!defaultPaymentMethodKey && paymentMethod?.value?.isDefault) ||
      false,
    color: paymentMethod?.value?.color,
    logo: paymentMethod?.value?.logo,
  });

  const [nameErrorMessage, setNameErrorMessage] = React.useState<string>('');
  const [institutionErrorMessage, setInstitutionErrorMessage] = React.useState<string>('');
  const cachedPaymentMethods = Data.paymentMethods.use();
  const [nameInputFocused, setNameInputFocused] = React.useState<boolean>(false);
  const [institutionInputFocused, setInstitutionInputFocused] = React.useState<boolean>(false);

  React.useEffect(() => {
    setNameErrorMessage('');
    setInstitutionErrorMessage('');
    // The duplicate error is stale once any field feeding the identity key changes. Leaving
    // it set would keep `onSave` returning early, so the user could never save the fix.
    setDuplicateErrorMessage('');

    const isUnchanged =
      paymentMethodValue.institution === paymentMethod?.value?.institution &&
      paymentMethodValue.digits === paymentMethod?.value?.digits;

    if (isUnchanged) {
      return;
    }

    // Institution and digits are what make two cards the same account, so a match here would
    // collide with an existing method. The name is deliberately not part of this — renaming a
    // duplicate does not make it a different card.
    const doesSameInstitutionAndDigitsExist = cachedPaymentMethods.some(
      (pm) =>
        pm.value.institution === paymentMethodValue.institution &&
        pm.value.digits === paymentMethodValue.digits &&
        pm.key !== selectedPaymentMethodKey,
    );

    if (!doesSameInstitutionAndDigitsExist) {
      return;
    }

    setDuplicateErrorMessage(getTranslation('This institution and digits already exist'));
  }, [paymentMethodValue.name, paymentMethodValue.digits, paymentMethodValue.institution]);

  function onSave() {
    if (nameErrorMessage || institutionErrorMessage || duplicateErrorMessage) {
      return;
    }

    try {
      // Admins and managers can reassign the payment method to another member. The owner
      // is tracked in two places that must stay in sync: the entity-level `createdBy`
      // (used for visibility/default filters) and `value.userID` (which feeds the derived
      // `primaryKey` used for dedup). Move both together, otherwise the method keeps the
      // original owner's identity key. Other roles leave the owner untouched.
      let resolvedUserID = paymentMethod?.value?.userID ?? userID;
      if (canReassign) {
        resolvedUserID = assignedToUserID;
      }

      const updatedPaymentMethodValue = {
        ...paymentMethodValue,
        userID: resolvedUserID,
      };

      // On create the owner (`createdBy`) may differ from the acting user, so also set
      // `updatedBy` to the acting user — otherwise it would mirror `createdBy` and the write
      // would fail the `updatedBy == auth.uid` rule. On update the store re-stamps updatedBy
      // anyway, so passing it here is harmless.
      const reassignFields = canReassign ? { createdBy: assignedToUserID, updatedBy: userID } : {};

      // Reassigning `value.userID` regenerates `primaryKey`. Guard against colliding with a
      // payment method the target member already owns (same institution/type/digits) — an
      // update can't merge the two docs, so it would leave duplicate keys.
      if (
        canReassign &&
        reassignmentCollidesWithExisting(
          updatedPaymentMethodValue,
          cachedPaymentMethods,
          selectedPaymentMethodKey,
        )
      ) {
        setDuplicateErrorMessage(getTranslation('This institution and digits already exist'));
        return;
      }

      let paymentMethodKey: string | undefined;
      if (paymentMethod) {
        Data.paymentMethods.update(paymentMethod.key, {
          value: updatedPaymentMethodValue,
          ...reassignFields,
        });
        paymentMethodKey = paymentMethod.key;
      } else {
        paymentMethodKey = Data.paymentMethods.create({
          value: updatedPaymentMethodValue,
          ...reassignFields,
        });
      }

      const paymentMethodIsAlreadyDefault = defaultPaymentMethodKey === paymentMethodKey;
      if (paymentMethodIsAlreadyDefault && !paymentMethodValue.isDefault) {
        updateMemberSettings(userID, { defaultPaymentMethodKey: null });
      } else if (
        !paymentMethodIsAlreadyDefault &&
        paymentMethodValue.isDefault &&
        paymentMethodKey
      ) {
        updateMemberSettings(userID, { defaultPaymentMethodKey: paymentMethodKey });
      }

      onDone();
    } catch (err: any) {
      if (!err?.issues) {
        alert(
          getTranslation(
            'There was an error saving the payment method. Try again or contact support.',
          ),
        );
        console.error(err);
        return;
      }

      const parsingError: any = err.issues.pop();
      if ((parsingError?.path as string[]).includes('name')) {
        setNameErrorMessage(getTranslation(`${parsingError.message}`));
      }
      if ((parsingError?.path as string[]).includes('institution')) {
        setInstitutionErrorMessage(getTranslation(`${parsingError.message}`));
      }
    }
  }

  return (
    <Layout.Column>
      <OpenSans.Primary size="xl-28" weight="bold-700">
        {getTranslation('Edit Payment Method')}
      </OpenSans.Primary>

      <Spacer.Vertical size={16} />

      <Layout.Row>
        <LabelTextField
          label="Institution"
          active={institutionInputFocused}
          error={!!institutionErrorMessage}
          errorMessage={institutionErrorMessage}
        >
          <OpenSans.Input
            autoFocus
            name="institution"
            value={paymentMethodValue?.institution ?? ''}
            type="text"
            placeholder="Bank of America"
            grow
            style={{ outline: 'none', width: 250 }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const institution = event.target.value;
              setPaymentMethodValue({ ...paymentMethodValue, institution });
            }}
            autoComplete="off"
            onFocus={() => setInstitutionInputFocused(true)}
            onBlur={() => setInstitutionInputFocused(false)}
          />
        </LabelTextField>
      </Layout.Row>

      <Spacer.Vertical size={16} />

      <Layout.Row>
        <LabelTextField
          label="Name"
          active={nameInputFocused}
          error={!!nameErrorMessage}
          errorMessage={nameErrorMessage}
        >
          <OpenSans.Input
            name="payment method name"
            value={paymentMethodValue?.name ?? ''}
            type="text"
            placeholder="Tom's Card"
            grow
            style={{ outline: 'none', width: 250 }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const name = event.target.value;
              setPaymentMethodValue({ ...paymentMethodValue, name });
            }}
            autoComplete="off"
            onFocus={() => setNameInputFocused(true)}
            onBlur={() => setNameInputFocused(false)}
          />
        </LabelTextField>

        <Spacer.Horizontal size={32} />

        <LabelTextField label="Last 4 Digits">
          <OpenSans.Input
            name="digits"
            value={paymentMethodValue?.digits ?? ''}
            type="text"
            placeholder="1234"
            maxLength={4}
            style={{ outline: 'none', width: 100 }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const digits = event.target.value.replace(/\D/g, '').slice(0, 4);
              setPaymentMethodValue({ ...paymentMethodValue, digits });
            }}
            autoComplete="off"
          />
        </LabelTextField>
      </Layout.Row>

      {duplicateErrorMessage ? (
        <>
          <Spacer.Vertical size={4} />
          <Layout.Row align>
            <Icon name="warning" color="red" size={16} />
            <Spacer.Horizontal size={8} />
            <OpenSans.Custom size="xs-12" weight="bold-700" color="destructive">
              {duplicateErrorMessage}
            </OpenSans.Custom>
          </Layout.Row>
        </>
      ) : null}

      {canReassign ? (
        <>
          <Spacer.Vertical size={16} />

          <Layout.Row>
            <SearchDropdown
              header={getTranslation('Assign to')}
              placeholder={getTranslation('Member Name')}
              selectedKey={assignedToUserID || null}
              autoCompleteValues={memberOptions}
              onSelect={(value: AutoCompleteValue | null) => {
                setDuplicateErrorMessage('');
                setAssignedToUserID(value?.key ?? userID);
              }}
              error={false}
              iconicIcon="people-circle-outline"
            />
          </Layout.Row>
        </>
      ) : null}

      <Spacer.Vertical size={32} />

      <Layout.Row justify="flex-end">
        <Button.Secondary
          onClick={onDiscard}
          radius={50}
          style={{
            border: '2px solid #fff',
            boxShadow: '0 0 4px rgba(255, 255, 255, 1)',
          }}
        >
          <Icon
            size={15}
            color={theme.colors.brandPrimary}
            style={{ paddingRight: 10 }}
            name="chevron-back"
          />
          <OpenSans.Primary
            size={15}
            weight="bold-700"
            style={{ color: theme.colors.brandPrimary }}
          >
            {getTranslation('Discard')}
          </OpenSans.Primary>
        </Button.Secondary>
        <Spacer.Horizontal size={32} />
        <Button.Primary
          onClick={onSave}
          radius={50}
          style={{
            border: '2px solid #fff',
            boxShadow: '0 0 8px rgba(255, 255, 255, .5)',
            width: 'auto',
          }}
        >
          <OpenSans.Custom size={15} weight="bold-700" style={{ color: theme.colors.buttonWhite }}>
            {getTranslation('Save Payment Method')}
          </OpenSans.Custom>
          <Icon
            size={15}
            color={theme.colors.buttonWhite}
            style={{ paddingLeft: 10 }}
            name="chevron-forward"
          />
        </Button.Primary>
      </Layout.Row>
    </Layout.Column>
  );
};

export const UpsertPaymentMethod: React.FC<{
  onDone: () => void;
  onDiscard: () => void;
  selectedPaymentMethodKey?: string;
}> = ({ onDone, onDiscard, selectedPaymentMethodKey }) => {
  const paymentMethod = Data.paymentMethods.useByKey(selectedPaymentMethodKey);

  // While editing, wait for the payment method to hydrate before mounting the form. The form
  // seeds its owner (`createdBy`) and value state once on mount; mounting before the data
  // loads would default the owner to the current user and silently reassign the card on save.
  if (selectedPaymentMethodKey && !paymentMethod) {
    return null;
  }

  return <PaymentMethodForm onDone={onDone} onDiscard={onDiscard} paymentMethod={paymentMethod} />;
};
